// src/components/Layout/CanvasArea/EditableTitle.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Check, X, Edit2 } from 'lucide-react';

interface EditableTitleProps {
  value: string;
  onChange: (newTitle: string) => void;
  placeholder?: string;
}

export const EditableTitle: React.FC<EditableTitleProps> = ({
  value,
  onChange,
  placeholder = 'Untitled Diagram'
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update edit value when prop changes
  useEffect(() => {
    setEditValue(value);
  }, [value]);

  // Focus input when entering edit mode
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = () => {
    const trimmedValue = editValue.trim();
    // If empty, revert to current value or use placeholder
    if (!trimmedValue) {
      setEditValue(value || placeholder);
      onChange(value || placeholder);
    } else {
      onChange(trimmedValue);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const displayValue = value || placeholder;

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        <input
          ref={inputRef}
          type="text"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          className="rounded border border-blue-400 bg-white px-2 py-1 text-sm text-gray-700 outline-none dark:border-blue-500 dark:bg-slate-800 dark:text-gray-300"
          maxLength={50}
          style={{ minWidth: '200px' }}
        />
        <button
          onClick={handleSave}
          className="rounded p-1 text-green-600 transition-colors hover:bg-green-50 dark:text-green-400 dark:hover:bg-green-950"
          title="Save (Enter)"
        >
          <Check className="h-4 w-4" />
        </button>
        <button
          onClick={handleCancel}
          className="rounded p-1 text-red-600 transition-colors hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950"
          title="Cancel (Esc)"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <button
      onClick={() => setIsEditing(true)}
      className="group flex items-center gap-2 rounded px-2 py-1 transition-colors hover:bg-gray-100 dark:hover:bg-slate-800"
      title="Click to edit diagram name"
    >
      <span className="text-sm text-gray-700 dark:text-gray-300">
        {displayValue}
      </span>
      <Edit2 className="h-3 w-3 text-gray-400 opacity-0 transition-opacity group-hover:opacity-100" />
    </button>
  );
};
